import React, { useCallback } from 'react';
import { ButtonType, NuvoButton, NuvoButtonProps } from '@nuvolo/nuux-core/components/NuvoButton';
import styled, { css } from 'styled-components';
import { color } from '@nuvolo/nuux-theme/utils';
import { borderRadius } from '../hooks/useClientTheme';
import FAIcon from './FAIcon';
import { useNavigate } from 'react-router-dom';
import { ClickEvent } from 'devextreme/ui/button';
import { useViewport } from '@nuvolo/nuux-theme/hooks/useViewport';

interface TypeStyles {
  'background-color': string;
  color: string;
  border: string;
  hover: {
    'background-color'?: string;
    color?: string;
    'text-decoration'?: string;
  };
  disabled: {
    'background-color'?: string;
    color?: string;
    'text-decoration'?: string;
  };
}

const typeStyles: Record<ButtonType, TypeStyles> = {
  submit: {
    'background-color': color('secondary', 'dark2'),
    color: color('custom', 'invertedText'),
    border: 'none',
    hover: {
      'background-color': color('primary', 'dark3'),
    },
    disabled: {
      'background-color': color('ui', 'light2'),
      color: color('custom', 'lightContrastText'),
    },
  },
  normal: {
    'background-color': color('ui', 'light2'),
    color: color('primary', 'dark3'),
    border: 'none',
    hover: {
      'background-color': color('ui', 'light'),
    },
    disabled: {
      'background-color': color('ui', 'light2'),
      color: color('custom', 'lightContrastText'),
    },
  },
  danger: {
    'background-color': color('accent', 'light3'),
    color: color('error', 'light'),
    border: 'none',
    hover: {
      'background-color': color('error', 'light3'),
    },
    disabled: {
      'background-color': color('ui', 'light2'),
      color: color('custom', 'lightContrastText'),
    },
  },
  secondary: {
    'background-color': 'transparent',
    color: color('primary', 'dark3'),
    border: 'none',
    hover: {
      'background-color': color('ui', 'light2'),
    },
    disabled: {
      'background-color': 'transparent',
      color: color('custom', 'lightContrastText'),
    },
  },
  link: {
    'background-color': 'transparent',
    color: color('info', 'light'),
    border: 'none',
    hover: {
      color: color('info', 'middle'),
      'text-decoration': 'underline',
    },
    disabled: {
      color: color('custom', 'lightContrastText'),
      'text-decoration': 'none',
    },
  },
};

const stylesToCss = (styles: Object) => {
  return css`
    ${Object.entries(styles)
      .filter(([key, value]) => !!value && typeof value === 'string')
      .map(([key, value]) => `${key}: ${value};`)
      .join('\n')}
  `;
};

interface StyledNuvoButtonProps {
  type: ButtonType;
  isIcon?: boolean;
}
const StyledNuvoButton = styled(NuvoButton)<StyledNuvoButtonProps>`
  &&.dx-button {
    height: 44px;
    width: 44px;
    border-radius: ${borderRadius?.s || '16px'} !important;
    box-shadow: none;
    opacity: 1;
    min-width: max-content;
    transition:
      background 0.2s,
      opacity 0.2s;

    ${({ type }) => stylesToCss(typeStyles[type])}

    &:hover {
      ${({ type }) => stylesToCss(typeStyles[type].hover)}
    }

    .dx-button-content {
      padding: 12px ${({ isIcon }) => (isIcon ? '12px' : '24px')};
      font-weight: 500;
      font-size: 14px;

      i {
        color: ${({ type }) => typeStyles[type].color};
      }
    }

    /* Custom disabled state styling */
    &.dx-state-disabled {
      cursor: not-allowed;
      box-shadow: none;

      ${({ type }) => stylesToCss(typeStyles[type].disabled)}
    }
  }
`;

export interface TMMButtonProps extends Omit<NuvoButtonProps, 'label'> {
  /**
   * Provide either `icon` or `label`.
   * When both are provided, the `responsive` will determine which to use.
   */
  icon?: string;

  /**
   * Provide either `icon` or `label`.
   * When both are provided, the `responsive` will determine which to use.
   */
  label?: string;

  /**
   * Use this when both `icon` and `label` are provided.
   * When `false` (default) - Icon will be used.
   * When `true` - On Desktop use Label, no Mobile use Icon.
   */
  responsive?: boolean;

  /**
   * Automatically navigate to provided path when button is clicked.
   */
  path?: string;
}

const TMMButton: React.FC<TMMButtonProps> = (props) => {
  const { icon, label = '', responsive, type = 'normal', onClick, path, ...btnProps } = props;

  const navigate = useNavigate();
  const buttonClicked = useCallback(
    (e: ClickEvent) => {
      if (path) {
        navigate(path);
      }

      if (onClick) {
        onClick(e);
      }
    },
    [onClick, path],
  );

  const { isDesktop } = useViewport();
  const isLabel = !icon || (!!label && isDesktop && responsive);

  // Wrap the label inside `<span>`.
  // Otherwise, if the `icon` is changed dynamically we get the error:
  // "Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node."
  const btnLabel = <span>{isLabel ? label : <FAIcon icon={icon} />}</span>;

  return (
    <StyledNuvoButton
      type={type}
      label={btnLabel as unknown as string}
      onClick={buttonClicked}
      isIcon={!isLabel}
      {...btnProps}
    />
  );
};

export default TMMButton;
